import {PropsWithChildren, useCallback, useEffect} from 'react';
import {DialogBackdrop, DialogPanel, Dialog as HeadlessDialog} from '@headlessui/react';
import clsx from 'clsx';
import {twMerge} from 'tailwind-merge';
import Spinner from '@/components/spinner';
import {SharedBackdrop, SharedBackdropProvider, useSharedBackdrop} from './backdrop';
import DialogContent from './content';
import DialogHeader from './header';

type DialogProps = PropsWithChildren<{
  isVisible: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onCloseTransitionEnded?: () => void;
  dialogClassName?: string;
  backdropClassName?: string;
  panelClassName?: string;
  sharedBackdrop?: boolean;
}>;

export type Handler = (event: TouchEvent | MouseEvent) => void;

const Dialog = ({
  children,
  isVisible,
  isLoading = false,
  dialogClassName,
  backdropClassName,
  panelClassName,
  sharedBackdrop = false,
  onClose,
  onCloseTransitionEnded,
}: DialogProps) => {
  // This prevents the "click outside" hook from triggering when we show a
  // dialog on top of everything.
  const listener: Handler = useCallback(
    event => {
      if (!isVisible) return;

      event.stopPropagation();
    },
    [isVisible],
  );

  useSharedBackdrop(sharedBackdrop && isVisible);

  useEffect(() => {
    document.addEventListener('mouseup', listener, {capture: true});
    document.addEventListener('touchend', listener, {capture: true});

    return () => {
      document.removeEventListener('mouseup', listener, {capture: true});
      document.removeEventListener('touchend', listener, {capture: true});
    };
  }, [listener]);

  return (
    <HeadlessDialog
      transition
      onTransitionEnd={onCloseTransitionEnded}
      onClose={onClose}
      open={isVisible}
      className={clsx(
        'relative z-50 animate-fade-in transition duration-200 ease-out data-[closed]:animate-none data-[closed]:opacity-0',
        dialogClassName,
      )}
    >
      {!sharedBackdrop && <DialogBackdrop className={clsx('fixed inset-0 bg-black/30', backdropClassName)} />}

      <div className="fixed inset-0 w-screen overflow-y-auto px-8 py-12">
        <div className="flex min-h-full items-center justify-center">
          <DialogPanel
            className={twMerge(
              clsx(
                'relative flex min-w-screen-sm max-w-xl flex-1 flex-col gap-8 rounded-3 bg-white px-8 py-12 shadow-dialog',
                panelClassName,
              ),
            )}
          >
            {children}
            {isLoading && (
              <div className="absolute inset-0 z-50 px-4 py-6">
                <div
                  className="flex size-full items-center justify-center backdrop-blur-sm"
                  data-testid="dialog-loading"
                >
                  <Spinner className="absolute inset-0" />
                </div>
              </div>
            )}
          </DialogPanel>
        </div>
      </div>
    </HeadlessDialog>
  );
};

Dialog.Header = DialogHeader;
Dialog.Content = DialogContent;
Dialog.SharedBackdropProvider = SharedBackdropProvider;
Dialog.SharedBackdrop = SharedBackdrop;

export default Dialog;
